/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import { useEventCallback, usePrevious } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

/**
 * Animated state of an element.
 */
export type AnimatedState = 'exited' | 'exiting' | 'entered' | 'entering';

/**
 * Hook to manage the animated state of a element based on "entered" state and
 * the animation events of the element.
 */
export function useAnimatedState(input: {
    isOpen: boolean;
    ref: React.RefObject<HTMLElement | null>;
}): AnimatedState {
    const { isOpen, ref } = input;
    const [state, setState] = React.useState<AnimatedState>('exited');
    const prevIsOpen = usePrevious(isOpen);

    React.useLayoutEffect(() => {
        if (isOpen) {
            setState('entering');
        } else if (prevIsOpen) {
            setState('exiting');
        }
    }, [prevIsOpen, isOpen]);

    React.useLayoutEffect(() => {
        const element = ref.current;

        if (!element || (state !== 'entering' && state !== 'exiting')) {
            return;
        }

        const onEnd = () => {
            switch (state) {
                case 'exiting':
                    setState('exited');
                    break;
                case 'entering':
                    setState('entered');
                    break;
            }
        };

        if (checkIsElementAnimating(element)) {
            function handleAnimationEnd(event: AnimationEvent) {
                if (event.target !== element) {
                    return;
                }

                onEnd();

                assert(element, 'Element must be defined');
                element.removeEventListener('animationend', handleAnimationEnd);
            }

            element.addEventListener('animationend', handleAnimationEnd);
            return () => {
                element.removeEventListener('animationend', handleAnimationEnd);
            };
        }

        onEnd();
    }, [state, ref]);

    return state;
}

/**
 * Check if an element is currently animating.
 */
function checkIsElementAnimating(element: HTMLElement) {
    const computedStyle = window.getComputedStyle(element);

    return (
        computedStyle.animationName &&
        computedStyle.animationName !== 'none' &&
        computedStyle.animation
    );
}

/**
 * Check if it's animating.
 */
export function checkIsAnimating(state: AnimatedState) {
    return state === 'entering' || state === 'exiting';
}

/**
 * Wait for the animation to finish.
 *
 * Store the state's resolve functions and call them when the animation associated with the given state finishes.
 */
export function useWaitForAnimation(state: AnimatedState) {
    const listenersRef = React.useRef<(() => void)[]>([]);

    const resolveListeners = useEventCallback(() => {
        listenersRef.current.forEach((handler) => handler());
        listenersRef.current.length = 0;
    });

    // Resolve animation listeners immediately if the state is not animating.
    React.useEffect(() => {
        if (!checkIsAnimating(state)) {
            resolveListeners();
        }
    }, [state, resolveListeners]);

    // Resolve animation listeners when the component unmounts.
    React.useEffect(() => resolveListeners, [resolveListeners]);

    return useEventCallback(async () => {
        await new Promise<void>((resolve) => {
            listenersRef.current.push(resolve);
        });
    });
}

export const AnimatedStateContext = React.createContext<AnimatedState | null>(null);
